import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import {
  BuildGalleryCoverAndIcon,
  BuildGalleryDetails,
  BuildsGallerySpecs,
} from "components/builds-gallery/builds-details/components";
import { BuildsGalleryPhotos } from "components/builds-gallery/builds-details/components/BuildsGalleryPhotos";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch } from "hooks/use-app-dispatch";
import modules from "modules";
import { useRouter } from "next/router";
import ShopLayout1 from "components/layouts/ShopLayout1";
import Navbar from "components/navbar/Navbar";
import { H6 } from "components/Typography";
import { BuildDetailsItem } from "modules/builds-gallery/types";
import React from "react";
import { localize } from "common";
import { useSnackBar } from "hooks";
import { usePcwUserCart } from "hooks/use-pcw-cart";
import { useLoginStatus } from "hooks/useLoginStatus";
import { DialugoeDecision } from "components/dialog/DialogueDecision";
import { useDialogueShow } from "hooks/useDialogue";
import { useBranchSelection } from "hooks/use-branch-selection";

// ===============================================================
type Props = {};
// ===============================================================

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  detailsContainer: {
    marginLeft: theme.spacing(7),
    marginRight: theme.spacing(7),
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(20),
    [theme.breakpoints.down("md")]: {
      marginLeft: theme.spacing(4),
      marginRight: theme.spacing(4),
    },
  },
}));

const { selectors: BuildsGallerySelectors, actions: BuildsGalleryActions } =
  modules.buildsGallery;

const BuildsGalleryPage: FC<Props> = ({}) => {
  const classes = useStyles();
  const router = useRouter();
  const snackbar = useSnackBar();
  const dispatch = useAppDispatch();
  const { addToCartMultiple, clearUserCart } = usePcwUserCart();
  const { isUserLogin } = useLoginStatus();
  const isLogin = isUserLogin;
  const { selectedBranchId } = useBranchSelection();

  const [isLoading, setIsLoading] = useState(false);
  const [buildDetails, setBuildDetails] = useState<BuildDetailsItem>();
  const [isItemsOutOfStockDialogVisible, setIsItemsOutOfStockDialogVisible] =
    useState<boolean>(false);
  const [outStocksItem, setOutStocksItem] = useState<string[]>([]);
  const [finalCartItems, setFinalCartItems] = useState([]); //get the item final item if out of stock item exist
  const [isDecision, setDecision] = useState<boolean>(false);

  const { showLoginFormOpen } = useDialogueShow();

  const title = useMemo(
    () => (buildDetails?.title ? `Gallery: ${buildDetails.title}` : ""),
    [buildDetails]
  );

  const getBuildDetails = useCallback(
    async (branch_id?: number) => {
      setIsLoading(true);
      const buildsUUID: any = router?.query?.id || "";
      if (buildsUUID) {
        const res = unwrapResult(
          await dispatch(
            BuildsGalleryActions.getBuildsDetailsThunk({
              uuid: buildsUUID,
              branch_id,
            })
          )
        );
        if (res?.success) {
          setIsLoading(false);
        }
        setBuildDetails(res?.originalData?.data);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch, router?.query?.id]
  );

  //warning dialogue
  const onClickWarningCart = useCallback(() => {
    setDecision(false);
    //set first cartInitialItems to be checked first
    const cartInitialItems = buildDetails?.components?.reduce(
      (accumulator, currVal) => {
        const index = accumulator.findIndex(
          (x) => x.product_slug === currVal.product_slug
        );
        if (index > 1) {
          ++accumulator[index].quantity;
        } else {
          accumulator.push({
            product_slug: currVal?.product_slug,
            product_name: currVal?.product_name,
            img_thumbnail: currVal?.product_thumbnail,
            amount: currVal?.retail_price,
            category_id: currVal?.category?.id,
            category: currVal?.category?.name,
            quantity: 1,
            stocks_left: currVal?.stocks,
          });
        }
        return accumulator;
      },
      []
    );

    const newOutOfStock = cartInitialItems.reduce((accumulator, currVal) => {
      if (currVal?.stocks_left === 0) {
        accumulator.push(currVal?.product_name);
      }
      return accumulator;
    }, []);

    //if out of stock item exist
    if (newOutOfStock.length > 0) {
      setIsItemsOutOfStockDialogVisible(true);
      setFinalCartItems(cartInitialItems); //pass to validate in agree dialog
      setOutStocksItem(newOutOfStock);
      return;
    }

    if (isLogin) {
      clearUserCart().then(() => {
        const userCartGallery = cartInitialItems.reduce(
          (accumulator, currVal) => {
            if (accumulator.stocks_left !== 0) {
              accumulator.push(currVal);
            }
            return accumulator;
          },
          []
        );
        addToCartMultiple(userCartGallery, selectedBranchId).then(() => {
          snackbar.show({
            severity: "info",
            message: localize.ITEMS_ADDED_CART,
          });
        });
      });
    }
  }, [
    addToCartMultiple,
    buildDetails?.components,
    clearUserCart,
    isLogin,
    selectedBranchId,
    snackbar,
  ]);

  const onClickAgreeCart = useCallback(() => {
    if (isLogin) {
      clearUserCart().then(() => {
        const cleanCart = finalCartItems?.filter(
          (item) => item.stocks_left > 0
        );

        addToCartMultiple(cleanCart, selectedBranchId).then(() => {
          snackbar.show({
            severity: "info",
            message: localize.ITEMS_ADDED_CART,
          });
        });

        setIsItemsOutOfStockDialogVisible(false);
      });
    }
  }, [
    addToCartMultiple,
    clearUserCart,
    finalCartItems,
    isLogin,
    selectedBranchId,
    snackbar,
  ]);

  const dialogueWarningClose = () => {
    setIsItemsOutOfStockDialogVisible(false);
  };

  const toggleDecision = () => {
    setDecision(!isDecision);
  };

  useEffect(() => {
    getBuildDetails(selectedBranchId);
  }, [getBuildDetails, selectedBranchId]);

  return (
    <ShopLayout1 title={title} navbar={<Navbar />}>
      <div className={classes.root} title="Build Details">
        {isLoading ? <LinearProgress color="secondary" /> : null}
        <BuildGalleryCoverAndIcon
          cover={buildDetails?.cover}
          badge={buildDetails?.badge}
        />
        <div className={classes.detailsContainer}>
          <BuildGalleryDetails
            isLoading={isLoading}
            buildDetails={buildDetails}
            onClickAddToCart={isLogin ? toggleDecision : showLoginFormOpen}
          />
          <BuildsGallerySpecs
            buildDetails={buildDetails}
            onClickAddToCart={isLogin ? toggleDecision : showLoginFormOpen}
          />
          {isLoading ? <LinearProgress color="secondary" /> : null}
          <BuildsGalleryPhotos
            photos={
              buildDetails && buildDetails?.photos ? buildDetails?.photos : []
            }
          />
        </div>
      </div>
      <div>
        <Dialog
          open={isItemsOutOfStockDialogVisible}
          onClose={dialogueWarningClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Out of Stock Item(s)"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Some item(s) in this PC Build went out of stock:
              <br></br>
              {outStocksItem.map((item, index) => (
                <Box mt={1} key={index}>
                  <H6>• {item}</H6>
                </Box>
              ))}
              <br></br>
              Do you still want to add this PC Build items on your cart?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={dialogueWarningClose} color="primary">
              Close
            </Button>
            <Button onClick={onClickAgreeCart} color="info" autoFocus>
              Add to Cart
            </Button>
          </DialogActions>
        </Dialog>
        <DialugoeDecision
          toggleOpen={isDecision}
          dialogueTitle={"Builds Gallery Cart"}
          dialogueContent={
            "Adding Builds Gallery item to cart requires to remove all current item(s) on your cart, Are you sure you want to add Builds Gallery item(s) in your cart?"
          }
          buttonTitle={"Confirm"}
          toggleOpenClose={toggleDecision}
          setFunction={onClickWarningCart}
        />
      </div>
    </ShopLayout1>
  );
};

export default BuildsGalleryPage;
